import React from 'react';
import styled from 'styled-components';

// ─── STATIC BACKGROUND ───────────────────────────────────────────────────────
// The ground does not animate, in any state. This constant is the invariant,
// and it is load-bearing rather than decorative: it gates the one transition
// SkyContainer still declares (see below). DWEB-25 established the reason — a
// very slow animation is still a RUNNING animation to Lighthouse, and a
// transition that can never fire is the same class of thing one level down.
//
// DWEB-170 removed what this used to gate. The stars, the shooting stars, the
// aurora curtains, the nebula blobs and the moon are gone outright, not
// hidden behind a flag: their DOM cost (205 nodes under SkyContainer) is most
// of what the change buys back.
const STATIC_BACKGROUND = true;
// ─────────────────────────────────────────────────────────────────────────────

// ─── THE GROUND ──────────────────────────────────────────────────────────────
// Black, lit by a fixed ambient wash: one large soft radial top-right, one
// fainter cool radial bottom-left. Light with no visible light source.
//
// Why not the aurora it replaces: #06010f → #130a2e → #071020 is dark enough
// that the glass tint, the panel shadow and the ground all landed within a few
// percent of each other, so heroGlassEffect had no edge to sit against — and
// 48px of blur erased the aurora it sat on, so the tint ended up doing the work
// the refraction should have been doing. The moon was the one element that
// genuinely proved the glass; the wash replaces what it contributed, without
// being a night-sky object on a neutral ground.
//
// Verbatim from the accepted study (DWEB-graphite-verbatim-2026-09-09.html,
// panel 01). Confluence: "Visual Direction — Graphite ground with ambient wash
// (v6.7.0)". Do not re-derive these numbers.
// OWNER'S CHANGE, 2026-09-08, mid-release: pure black, not graphite.
//
// This REVERSES the Confluence direction, which considered flat black #08090B
// and rejected it — "highest text contrast, least glass; no chroma for
// saturate(), no detail for blur; the panel becomes a hairline card".
//
// That prediction was MEASURED against the real build and does not hold. It
// was reasoned about flat black with NO wash; with the wash kept, the panel
// separates from the ground BETTER on black than on graphite, because the
// glass's white rim highlights are absolute while the ground beneath them
// darkens. Panel-edge vs ground-just-outside, mean luminance, 1280x720:
//
//   graphite #1C1C1E   ground 80.7   edge 96.7   ΔL 16.0   ratio 1.19
//   black    #08090B   ground 65.9   edge 83.6   ΔL 17.7   ratio 1.25
//   black    #000000   ground 59.0   edge 77.6   ΔL 18.5   ratio 1.29
//
// The wash therefore matters MORE here, not less: on this ground it is the
// only luminance the rim has to catch. Do not drop it as "invisible on black".
const GROUND = '#000000';
const AMBIENT_WASH = `
  radial-gradient(60% 55% at 80% 4%,  rgba(226,236,255,.20) 0%, rgba(226,236,255,.05) 45%, transparent 72%),
  radial-gradient(52% 46% at 10% 98%, rgba(120,150,255,.10) 0%, transparent 70%)
`;
// ─────────────────────────────────────────────────────────────────────────────

const SkyContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -10;
  background: ${GROUND};
  ${!STATIC_BACKGROUND && 'transition: background 2s ease-in-out;'}
  pointer-events: none;
  overflow: hidden;
`;

// One element, two gradients, nothing animated and nothing promoted: gradients
// do not create a compositor layer on their own, and there is no filter,
// transform or will-change here to make one.
const AmbientWash = styled.div`
  position: absolute;
  inset: 0;
  background: ${AMBIENT_WASH};
  pointer-events: none;
`;

/**
 * The site's ground. DWEB-170: black under a fixed ambient wash, replacing
 * the aurora night sky.
 *
 * The contract is unchanged and is what the suite identifies this element by:
 * always mounted, position:fixed, z-index:-10, pointer-events:none.
 *
 * Video mode was removed in DWEB-23 — the branch was unreachable, d5114b2
 * hard-wired the animated path in 2026-04 because the mp4 cost load time and
 * battery. Re-enabling video is a product decision, not a revert.
 */
const DynamicSkyBackground = () => (
  <SkyContainer>
    <AmbientWash />
  </SkyContainer>
);

export default DynamicSkyBackground;
